<template>
  <v-dialog v-model="dialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-bank-transfer</v-icon>
        Forward to Another Branch
      </v-card-title>

      <v-card-text class="pt-6">
        <v-alert type="info" dense outlined class="mb-4">
          <div class="text-body-2">
            <strong>Note:</strong> Forwarding this {{ itemType }} will also
            transfer all associated Supplier Purchase Orders.
          </div>
        </v-alert>

        <v-form ref="forwardForm" v-model="valid">
          <v-row>
            <v-col cols="12">
              <div class="text-subtitle-2 mb-2 font-weight-bold">
                Current Branch:
                <v-chip color="blue" dark small class="ml-2">
                  {{ currentBranch }}
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="selectedBranch"
                :items="availableBranches"
                item-text="branch_name"
                item-value="id"
                label="Select Target Branch"
                prepend-icon="mdi-source-branch"
                :rules="[rules.required]"
                outlined
                dense
              >
                <template v-slot:selection="{ item }">
                  <v-chip color="success" small>
                    {{ item.branch_name }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="reason"
                label="Reason for Forwarding (Optional)"
                prepend-icon="mdi-comment-text"
                outlined
                dense
                rows="3"
                counter
                maxlength="500"
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-alert type="warning" dense outlined>
                <div class="text-body-2">
                  <strong>Warning:</strong> This action cannot be undone. The
                  {{ itemType }}
                  and all related records will be permanently moved to the
                  selected branch.
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-btn
          color="primary"
          :disabled="!valid || forwarding"
          :loading="forwarding"
          @click="confirmForward"
        >
          <v-icon left>mdi-check</v-icon>
          Confirm Forward
        </v-btn>
        <v-btn text @click="close" :disabled="forwarding">
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "ForwardToBranchModal",

  data() {
    return {
      dialog: false,
      valid: false,
      forwarding: false,
      selectedBranch: null,
      reason: "",
      item: null,
      itemType: "", // e.g. 'Customer Purchase Order'

      rules: {
        required: (value) => !!value || "Please select a branch",
      },
    };
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      branches: "branch/branches",
    }),

    currentBranch() {
      if (!this.item || !this.item.branch_id) return "N/A";
      const branch = this.branches.find((b) => b.id === this.item.branch_id);
      return branch ? branch.branch_name : "Unknown";
    },

    availableBranches() {
      if (!this.item) return [];
      // Exclude the current branch from the list
      return this.branches.filter((b) => b.id !== this.item.branch_id);
    },
  },

  methods: {
    ...mapActions({
      get_branches: "branch/get_branches",
    }),

    open(item, itemType) {
      this.item = item;
      this.itemType = itemType;
      this.selectedBranch = null;
      this.reason = "";
      this.dialog = true;

      // Ensure branches are loaded
      if (this.branches.length === 0) {
        this.get_branches();
      }
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.item = null;
        this.itemType = "";
        this.selectedBranch = null;
        this.reason = "";
        if (this.$refs.forwardForm) {
          this.$refs.forwardForm.reset();
        }
      }, 300);
    },

    async confirmForward() {
      if (!this.$refs.forwardForm.validate()) {
        return;
      }

      const confirmed = await this.$confirm({
        title: "Forward to Branch",
        message: `Are you sure you want to forward this ${this.itemType} to ${
          this.branches.find((b) => b.id === this.selectedBranch)?.branch_name
        }?<br><br>This will also transfer all associated supplier purchase orders.`,
        color: "success",
        icon: "mdi-check",
      });

      if (!confirmed) return;

      this.forwarding = true;

      try {
        const payload = {
          id: this.item.id,
          target_branch_id: this.selectedBranch,
          reason: this.reason,
        };

        this.$emit("forward", payload);
      } catch (error) {
        console.error("Error in forward confirmation:", error);
        this.$emit(
          "error",
          error.response?.data?.message || "Failed to forward item"
        );
      } finally {
        this.forwarding = false;
      }
    },

    resetForwarding() {
      this.forwarding = false;
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}

ul {
  list-style-type: disc;
}
</style>
